import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * Filtro de lista com seleção múltipla e busca.
 * Props:
 * - options: string[] - Lista de opções
 * - values: string[] - Opções marcadas; vazio significa "todas"
 * - onChange: fn(string[]) - Recebe a nova seleção
 * - placeholder: string - Texto do botão sem nada marcado (ex.: "Todos os produtos")
 * - label: string - Rótulo no plural, usado na contagem (ex.: "Produtos: 3")
 * - icon: componente lucide opcional
 * - buttonId: id do botão que abre a lista
 */
export default function MultiSelectFilter({
  options = [],
  values = [],
  onChange,
  placeholder = 'Selecionar',
  label = '',
  icon: Icon = null,
  buttonId,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter((opt) => opt.toLowerCase().includes(term));
  }, [options, searchTerm]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleValue = (value) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
      return;
    }
    onChange([...values, value]);
  };

  const clearAll = () => onChange([]);

  let resumo = placeholder;
  if (values.length === 1) {
    [resumo] = values;
  } else if (values.length > 1) {
    resumo = `${label}: ${values.length}`;
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        id={buttonId}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title={values.length > 1 ? values.join(', ') : undefined}
        className="w-full inline-flex items-center justify-between gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2.5 text-sm text-[color:var(--text-primary)] shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)]" />}
          <span className="truncate">{resumo}</span>
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label={label || placeholder}
          className="absolute z-50 mt-2 w-full rounded-md bg-[color:var(--surface)] border border-[color:var(--border)] shadow-sm"
        >
          <div className="p-2 border-b border-[color:var(--border)] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-[color:var(--text-muted)]">{label}</span>
              {values.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-[color:var(--accent)] hover:opacity-80"
                >
                  Limpar
                </button>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                aria-label={`Buscar ${label.toLowerCase()}`}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:opacity-80"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-60 overflow-auto p-1">
            {filtered.length > 0 ? (
              filtered.map((opt) => {
                const isSelected = values.includes(opt);
                return (
                  <label
                    key={opt}
                    className="flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-md"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleValue(opt)}
                      className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                    />
                    <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                      {opt}
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">Sem resultados</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
